import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useApi } from '@/lib/ConfigProvider'
import { useOnboarding } from '@/onboarding/OnboardingProvider'
import { fetchAccountDetail, fetchAccounts, STATE_LABEL } from '@/accounts/accounts'
import type { Account, AccountDetail, AccountsState } from '@/accounts/accounts'
import { fetchBalance, isStale } from '@/accounts/balances'
import type { BalanceState } from '@/accounts/balances'
import { formatMoney } from '@/lib/money'
import { CURRENCY_NAME, CURRENCY_SYMBOL } from '@/lib/currencyNames'
import './Balances.css'

/**
 * The accounts a customer holds (`#50`, backend `go-wallet#379`).
 *
 * The first screen in this app that renders something the backend actually
 * answers with rather than a fixture. Everything on it comes from the response:
 * whether the account can be paid into, what to say about its state, and
 * whether its IBAN has arrived. None of it is derived here — deriving it is an
 * authorisation decision in a browser bundle (invariant 8), and the contract
 * spells out that `can_receive` exists precisely "so a client cannot show a
 * control that always fails".
 *
 * # It now shows a balance, and until today it was right not to (`#148`)
 *
 * `GET /v1/accounts` still carries no figure — its own description says "no
 * balance, no transactions and no statement — those are their own surfaces" —
 * and when this screen was built that surface did not exist. A screen about
 * accounts and IBANs was the honest maximum, and inventing a zero would have
 * been worse than an absence.
 *
 * `GET /v1/accounts/{account_ref}/balance` landed with backend `go-wallet#972`,
 * so the absence stopped being honest: this was the only screen in the app
 * named after a figure it did not render, while `/` showed that same figure for
 * the same account from the same session.
 *
 * ONE READ PER ACCOUNT, FANNED OUT AND COLLECTED BY REFERENCE, the same shape
 * `Overview` uses. Not one aggregate call: there isn't one, and more
 * importantly a single failure must not blank the others — a customer with two
 * accounts and one unreadable balance still needs the other figure and both
 * IBANs.
 */
export function Balances() {
  const api = useApi()
  const [state, setState] = useState<AccountsState>({ status: 'loading' })

  // Keyed by account reference rather than by index, so a row and its figure
  // cannot be paired by position — the accounts list is re-fetched on retry and
  // nothing promises the order is stable.
  const [balances, setBalances] = useState<Record<string, BalanceState>>({})

  // Read once per render pass rather than per row, so two rows cannot disagree
  // about whether the same instant is stale.
  const [now] = useState(() => new Date())

  // The same guard as OnboardingProvider (#57), and here it is DEFENCE rather
  // than a fix.
  //
  // Overlapping reads are not reachable through this screen today: `load` sets
  // `loading` before awaiting, which removes the "Try again" button, so a second
  // click cannot happen while the first is in flight. OnboardingProvider does
  // not do that, which is exactly why the defect is live there and not here.
  //
  // It stays because the property being relied on is a rendering detail, not a
  // guarantee — anyone who later keeps the control visible during a reload, or
  // adds a second caller of `load`, reintroduces the failure silently. Nine
  // lines is a cheap price for not having that depend on a `&&`.
  const generation = useRef(0)

  const load = useCallback(async () => {
    const mine = ++generation.current
    setState({ status: 'loading' })
    const next = await fetchAccounts(api)
    if (mine !== generation.current) return
    setState(next)
    if (next.status !== 'ready') return

    // Every row starts at `loading` in one write, so a row cannot render
    // "No figure just now" for the moment before its own read is dispatched —
    // which is the same claim as a failure and is not true yet.
    setBalances(
      Object.fromEntries(
        next.accounts.map((a) => [a.accountRef, { status: 'loading' } as BalanceState]),
      ),
    )
    for (const account of next.accounts) {
      void fetchBalance(api, account.accountRef).then((b) => {
        if (mine !== generation.current) return
        setBalances((prev) => ({ ...prev, [account.accountRef]: b }))
      })
    }
  }, [api])

  useEffect(() => {
    void load()
  }, [load])

  return (
    <section className="accounts">
      <header className="accounts__head">
        <h1 className="accounts__title">Balances</h1>
        <p className="accounts__lede">
          The account the institution issued to you, and — where it can be paid into — the details
          somebody paying you needs.
        </p>
      </header>

      {state.status === 'loading' && <p className="accounts__muted">Reading your accounts…</p>}

      {/*
        Four different absences, four different sentences. "You hold no
        account", "you have no wallet yet" and "we could not look" are the same
        empty screen and mean entirely different things about what the customer
        should do next — which is the distinction #22 was filed for.
      */}
      {state.status === 'unregistered' && (
        <div className="accounts__notice" role="status">
          <p className="accounts__noticeTitle">Your wallet is not open yet.</p>
          <p className="accounts__muted">
            An account is issued once the institution has verified who you are.
          </p>
          <Link className="accounts__link" to="/welcome">
            See where your checks stand
          </Link>
        </div>
      )}

      {state.status === 'none' && <NoAccountYet />}

      {state.status === 'unavailable' && (
        <div className="accounts__notice" role="status">
          <p className="accounts__noticeTitle">We could not read your accounts.</p>
          {/*
            Deliberately NOT "you have no account". We do not know that, and the
            contract separates the two for the same reason: telling a customer
            their account does not exist because a read failed is a false
            statement about their own money.
          */}
          <p className="accounts__muted">{state.reason}</p>
          <button type="button" className="accounts__ghost" onClick={() => void load()}>
            Try again
          </button>
        </div>
      )}

      {state.status === 'ready' && (
        <div className="ledger">
          <div className="ledger__head">
            <h2 className="ledger__title">Accounts</h2>
            <span className="ledger__count">
              {state.accounts.length} {state.accounts.length === 1 ? 'account' : 'accounts'}
            </span>
          </div>
          <div className="ledger__cols" aria-hidden="true">
            <span>Account</span>
            <span>Status</span>
          </div>
          <ul className="ledger__rows">
            {state.accounts.map((a, i) => (
              <AccountRow
                key={a.accountRef}
                account={a}
                lead={i === 0}
                balance={balances[a.accountRef] ?? { status: 'loading' }}
                now={now}
              />
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}

/**
 * An empty account list is not one situation, and the copy used to claim it was
 * (#101).
 *
 * It said, always: *"This is normal while the institution is still working
 * through your application. Nothing is required from you."* An empty list is
 * exactly the set of customers who are registered and not yet accepted, and that
 * set contains four states:
 *
 *   pending             they have to start identity verification. Something IS
 *                       required from them.
 *   more_info_required  the institution has ASKED for something. The old copy
 *                       said the opposite in as many words, directly under
 *                       AppShell's banner saying so, in the same paint.
 *   in_review           genuinely nothing to do. The old copy was right here,
 *                       and only here.
 *   rejected            nothing is "still working through" anything, and no
 *                       account is coming.
 *
 * So: an exhaustive switch, so a new state is a compile error rather than
 * inheriting the friendliest available sentence — which is precisely how #54 and
 * #70 happened. This screen says where things stand in one line and links to
 * `/welcome` rather than restating the journey.
 */
function NoAccountYet() {
  const { state } = useOnboarding()

  const note = ((): string => {
    if (state.status === 'loading') return 'Checking where your application stands…'
    // Not a guess in either direction. #22's whole point.
    if (state.status === 'unavailable') return 'We could not read where your application stands.'
    if (state.status === 'unregistered') {
      return 'Your wallet is not open yet — we still need your country and legal name.'
    }
    switch (state.onboarding.state) {
      case 'pending':
        return 'Your identity has not been verified yet, and that is the next step.'
      case 'more_info_required':
        // The one the old sentence was flatly wrong for.
        return 'The institution has asked you for something before it can go further.'
      case 'in_review':
        return 'The institution is working through your application. Nothing is required from you.'
      case 'rejected':
        return 'The institution could not accept your application, so no account was issued.'
      case 'accepted':
        // Accepted with no account is a real gap, not a contradiction: issuance
        // follows acceptance and is not instant. Saying "nothing is required"
        // is true here; saying it is "being worked through" would not be.
        return 'Your application was accepted. The account itself has not been issued yet.'
    }
  })()

  return (
    <div className="accounts__notice" role="status">
      <p className="accounts__noticeTitle">No account has been issued to you yet.</p>
      <p className="accounts__muted">{note}</p>
      <Link className="accounts__link" to="/welcome">
        See where your checks stand
      </Link>
    </div>
  )
}

/**
 * How a currency is shown in the row's avatar and name.
 *
 * Presentation only — nothing here scales, rounds or compares an amount, and
 * `money.ts` remains the only place that knows what a currency MEANS. The
 * fallbacks are the point: an unknown code renders as itself rather than as a
 * blank circle, which is the same rule `evidenceLabel` follows and the opposite
 * of what #100 had to undo.
 */

/**
 * One account's figure on its row (`#148`).
 *
 * # A failure is never a zero
 *
 * `503 balance_unavailable` covers a stale snapshot, a missing wallet and a
 * read failure as ONE answer, deliberately — so there is nothing to
 * distinguish and nothing to assume. A zero would say the account is empty,
 * which is a different claim and a much worse one to make wrongly.
 *
 * # `available` is never recomputed here
 *
 * It is `min(balance − pending_out, the institution's figure)` and the backend
 * computes it with the same code the money path uses. A second projection in a
 * browser bundle would eventually disagree with the one that governs a
 * cash-out, and the customer would be shown money they cannot spend.
 *
 * # TWO FIGURES ONLY WHEN THE GAP IS ONE A BANK WOULD SHOW (#174)
 *
 * This row rendered `balance` over `available` unconditionally, and on a live
 * account that read `100,000.00 / 93,602.86 EUR available`. Reported from the
 * screen, and the question is the right one:
 *
 *   "100,000 but 93,000 available, in what period is this? is it 100,000
 *    forever? what if i get another 100,000 and spend them all what then? is
 *    it going to be 200,000 but 0 remaining"
 *
 * Yes, it would have been. And that is the defect, not the wording.
 *
 * On a bank statement "balance" and "available" differ by money that is
 * COMMITTED AND WILL SETTLE — a card authorisation, a transfer in flight. It
 * is a temporary state and it resolves. `pending_out` is that gap here, and
 * showing it is right.
 *
 * `capped_by: "provider"` is not that. It means the institution reports LESS
 * than our own book, which is a reconciliation break: money left and our books
 * never recorded it. Nothing resolves it, and every future credit widens the
 * pair rather than closing it. Rendering it in the shape a customer reads as
 * "a hold that will clear" is a category error, and it makes our own figure
 * look like an account balance when it is an internal number that has drifted
 * from the account.
 *
 * So the institution's figure IS the balance when the two disagree. It is the
 * one the money path already enforces, the one a cash-out is measured against,
 * and the only one that describes the account. The gap is an operations
 * problem — `reconcile.StrandedDebit` pages somebody about it (go-wallet#853)
 * — and not a subtitle.
 */
function RowBalance({ balance, now }: { balance: BalanceState; now: Date }) {
  if (balance.status !== 'ready') {
    return (
      <span className="ledger__figure ledger__figure--none">
        {balance.status === 'loading' ? '…' : 'No figure just now'}
      </span>
    )
  }
  const b = balance.balance

  // The institution's figure when it is the binding one, our book's otherwise.
  // Never both as a pair: see the header.
  const headline = b.cappedBy === 'provider' ? b.available : b.balance

  // The SECOND line exists only for a gap that behaves like one — money already
  // committed to a payment, which will settle and close it.
  const committed = b.pendingOut.amountMinor !== 0n
  const stale = isStale(b.providerAsOf, now)

  return (
    <span className="ledger__figure">
      <span className="ledger__amount tnum">{formatMoney(headline)}</span>
      {committed || stale ? (
        <span className="ledger__avail tnum">
          {committed
            ? `${formatMoney(b.available, { withCurrency: true })} available · ${formatMoney(b.pendingOut)} on its way out`
            : formatMoney(b.available, { withCurrency: true }) + ' available'}
          {stale ? ' · not up to the minute' : ''}
        </span>
      ) : null}
    </span>
  )
}

function AccountRow({
  account,
  lead,
  balance,
  now,
}: {
  account: Account
  lead: boolean
  balance: BalanceState
  now: Date
}) {
  const api = useApi()
  const [detail, setDetail] = useState<AccountDetail | null>(null)
  const [failed, setFailed] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const reveal = useCallback(async () => {
    setBusy(true)
    setFailed(null)
    try {
      const got = await fetchAccountDetail(api, account.accountRef)
      if (typeof got === 'string') setFailed(got)
      else setDetail(got)
    } catch (e) {
      // `fetchAccountDetail` is typed to return a reason rather than throw, and
      // #72 was it throwing anyway. The try is here because a control that can
      // never leave "Fetching…" is a dead end with no remount, no timer and no
      // retry — the same shape as #56 — and a signature is a promise, not a
      // guarantee.
      setFailed(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }, [api, account.accountRef])

  const [open, setOpen] = useState(false)

  // ONE OBJECT, NOT A MIX OF TWO (#102).
  //
  // `AccountDetail extends Account`, so the detail response is a strict superset
  // of the list row — a fresh `state`, `can_receive`, `iban_status` and `notice`
  // arrive with the IBAN. This card used to read exactly three fields off it
  // (`iban`, `bic`, `holderName`) and keep rendering everything else from the
  // list row, which is fetched ONCE: `load` is keyed on `[api]`, a stable
  // context value, so a tab left open holds its first answer indefinitely.
  //
  // The result was the freshest fact on the screen and the stalest sitting in
  // one card — a full account number under a badge saying `active`, for an
  // account frozen since page load, with the stale half being the one that says
  // whether the number is safe to hand to a payer.
  //
  // Rendering one object REMOVES the possibility rather than correcting it. The
  // card shows either the list row or the fresher one, and cannot show halves of
  // each.
  const shown: Account = detail ?? account
  // Against the ORIGINAL list row, not against `shown` — `shown` IS `detail`
  // once it arrives, so comparing them would be comparing a value with itself
  // and this would be permanently false.
  const stateChanged = detail !== null && detail.state !== account.state

  const name = CURRENCY_NAME[shown.currency] ?? shown.currency
  const symbol = CURRENCY_SYMBOL[shown.currency] ?? shown.currency
  const pending = shown.ibanStatus === 'pending'

  return (
    <li className={`ledger__row${open ? ' ledger__row--open' : ''}`}>
      <div className="ledger__line">
        {/*
          The design's ledger idiom: a currency avatar, a two-line identity, and
          the status right-aligned. The first row carries the accent fill, as
          Holdings does in docs/design — it marks the primary account without
          adding a word to the screen.
        */}
        <span
          className={`ledger__avatar${lead ? ' ledger__avatar--lead' : ''}${
            symbol.length > 2 ? ' ledger__avatar--code' : ''
          }`}
          aria-hidden="true"
        >
          {symbol}
        </span>

        <span className="ledger__id">
          <span className="ledger__name">{name}</span>
          {/*
            Branch on `iban_status`, never on whether a string is present. An
            account exists before its IBAN does — `pending` is the normal early
            state, not a fault.
          */}
          {/*
            THE REASON COMES BEFORE THE NUMBER (#73), AND IT MUST NOT BE BEHIND
            THE DISCLOSURE.
            The contract: "Do not treat the presence of an account as permission
            to advertise it for payment: branch on `can_receive`, and render
            `notice` for the reason." Moving the full sentence into the expanded
            panel — which the first draft of this row did — put a masked account
            number on screen with no restriction visible until the customer
            opened it. That is the advertisement the rule forbids, reached by a
            new route.
            So the row itself carries a short marker, before the number in DOM
            order as well as visually, and the panel carries the full sentence.
            Not hidden and not removed: a closed account still needs its
            reference for a wind-out, which is why the number stays at all.
          */}
          <span className="ledger__subline">
            {!shown.canReceive && <span className="ledger__nopay">Cannot be paid into</span>}
            <span
              className={`ledger__sub${pending ? '' : ' ledger__sub--iban'}`}
              // The row truncates by design -- see the panel comment. A `title`
              // is not the fix (it is unreachable by touch and by keyboard) but
              // it costs nothing and helps a pointer user who never opens the
              // panel.
              title={pending ? (shown.ibanNote ?? undefined) : undefined}
            >
              {pending
                ? (shown.ibanNote ?? 'Your account number is on its way.')
                : (shown.ibanMasked ?? '—')}
            </span>
          </span>
        </span>

        <span className="ledger__right">
          {/*
            THE STATE CHIP STAYS FIRST, before the figure in DOM order as well as
            visually (`#73`). The rule was written about the account number — a
            masked IBAN with no restriction visible is an advertisement for an
            account that cannot be paid into — and a money figure reaches the
            same place by a shorter road: an amount shown above a restriction
            reads as money somebody can use.
          */}
          <span className={`chip chip--${shown.state}`}>{STATE_LABEL[shown.state]}</span>
          <RowBalance balance={balance} now={now} />
          <button
            type="button"
            className="ledger__toggle"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? 'Hide' : 'Details'}
          </button>
        </span>
      </div>

      {open && (
        <div className="ledger__panel">
          {/*
            Said out loud rather than swapped quietly. A customer who read
            "active" a moment ago and now reads "frozen" should be told it moved,
            not left to doubt what they saw.
          */}
          {stateChanged && (
            <p className="ledger__changed" role="status">
              This account&rsquo;s state changed while this page was open, and the details below are
              the current ones.
            </p>
          )}

          {/*
            The backend's words for this account's state, verbatim. Ours would be
            a second wording for the same fact, and a customer who reads one thing
            here and another in an email is a customer who calls.
          */}
          {shown.notice.headline !== '' && (
            <p className="ledger__noticeTitle">{shown.notice.headline}</p>
          )}
          {shown.notice.detail !== '' && <p className="ledger__muted">{shown.notice.detail}</p>}
          {/*
            THE BACKEND'S OWN SENTENCE, IN FULL (#122).

            `iban_note` exists "so there is one wording rather than one per
            client" (go-wallet#310, FR-20.1) and the contract says to render it
            verbatim. The row above shows it in `.ledger__sub`, which is
            `nowrap` + ellipsis, and the real catalogue sentence is 142
            characters -- truncated at EVERY width including 1440, the design's
            own canvas. Until now it appeared nowhere else and carried no
            `title`, so the one sentence written for exactly this state was the
            one thing on the screen a customer could not finish reading.

            Rendered here rather than by unwrapping the row: the row is a scan
            line and a wrapping sentence in it would push every account below it
            down by a variable amount. The panel is where the full wording of
            everything else on this screen already lives.
          */}
          {pending && shown.ibanNote !== undefined && shown.ibanNote !== '' && (
            <p className="ledger__muted">{shown.ibanNote}</p>
          )}
          {shown.notice.supportPath && (
            <p className="ledger__muted">This is something our support team can help with.</p>
          )}

          {/*
            THE REASON COMES FIRST WHEN THE ACCOUNT CANNOT BE PAID INTO (#73).
            The contract says plainly: "Do not treat the presence of an account
            as permission to advertise it for payment: branch on `can_receive`,
            and render `notice` for the reason." This screen used to print the
            IBAN, offer the full details, and mention the restriction last —
            which is the advertisement it warns about, with the contradiction
            placed after it.
          */}
          {!shown.canReceive && (
            <p className="ledger__warn" role="status">
              This account cannot be paid into at the moment, so these details will not receive
              money.
            </p>
          )}

          {pending ? null : detail === null ? (
            /*
              The full number is fetched only when asked for. The list route
              deliberately withholds it so that polling for issuance does not
              ship an account number on every tick, and pulling it on every
              render would hand that property straight back.
            */
            <button
              type="button"
              className="ledger__reveal"
              onClick={() => void reveal()}
              disabled={busy}
            >
              {busy ? 'Fetching…' : shown.canReceive ? 'Show full details' : 'Show account details'}
            </button>
          ) : (
            <dl className="ledger__details">
              <div>
                <dt>IBAN</dt>
                <dd className="ledger__mono">{detail.iban ?? '—'}</dd>
              </div>
              <div>
                <dt>BIC</dt>
                <dd className="ledger__mono">{detail.bic ?? '—'}</dd>
              </div>
              <div>
                <dt>Account holder</dt>
                {/*
                  The institution's spelling of the name, not ours. It is what a
                  paying platform's form has to match, and where the two differ
                  theirs is the one that gets the payment through.
                */}
                <dd>{detail.holderName ?? '—'}</dd>
              </div>
            </dl>
          )}

          {failed !== null && (
            <p className="ledger__warn" role="alert">
              We could not fetch the full details. {failed}
            </p>
          )}
        </div>
      )}
    </li>
  )
}
